import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect, IOptionType } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';
import { IStorageLocationFormValues } from '../../../../@types/model/masterData/storageLocation/storageLocationFormValues';
import StorageLocationModelHelper from '../../../../@types/model/masterData/storageLocation/storageLocationModelHelper';
import { IStorageUnit } from '../../../../@types/model/masterData/storageUnit/storageUnit';
import OptionType from '../../../../@types/model/optionType';

interface IStorageLocationFormProps {
    isLoading : boolean;
    initialValues : IStorageLocationFormValues;
    onSubmit : (values : IStorageLocationFormValues) => void;
    onClose : () => void;
    storageUnits : Array<IStorageUnit>;
}

const StorageLocationForm = (props : IStorageLocationFormProps) : React.ReactElement => {
    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<IStorageLocationFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(StorageLocationModelHelper.formSchema()),
        mode: 'onChange',
    });

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const storageUnitOptions = useMemo<Array<IOptionType>>(() => {
        return props.storageUnits.filter(x => x.isActive).map(OptionType.fromDataModel);
    }, [
        props.storageUnits
    ]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p20 pt15 pb10'}>
                    <ControlledTextField
                        className={'mb10'}
                        control={control}
                        label={'Code'}
                        name={'code'}
                        defaultValue={props.initialValues.code}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb20'}
                        control={control}
                        label={'Description'}
                        name={'description'}
                        defaultValue={props.initialValues.description}
                        size={'small'}
                    />
                    <ControlledMuiAutoCompleteSelect
                        className={'mb20'}
                        control={control}
                        label={'Storage Units'}
                        name={'storageUnits'}
                        isMulti
                        defaultValues={props.initialValues.storageUnits}
                        options={storageUnitOptions}
                    />
                    <div className={'flx1 fdr jcfs'}>
                        <ControlledLabelledCheckBox
                            label={'Active?'}
                            name={'isActive'}
                            control={control}
                            defaultValue={props.initialValues.isActive}
                            disabled={props.initialValues.isActive}
                        />
                    </div>
                    <div className={'fdr flx1 aic jcfe'}>
                        <Button className={'cpd mr20'} type={'button'} onClick={props.onClose}>
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={ !isDirty || !isValid || props.isLoading }
                        />
                    </div>
                </div>
            </form>
            <CustomReactHookFormDevTools control={control} />
        </div>
    );
};

export default StorageLocationForm;
